import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Shield, Save, Key, Globe, Eye, EyeOff, CheckCircle, RefreshCcw } from 'lucide-react';

const GlobalConfig = () => {
    const [config, setConfig] = useState({
        darajaConsumerKey: '',
        darajaConsumerSecret: '',
        darajaPasskey: '',
        isLive: false
    });
    const [showKeys, setShowKeys] = useState(false);
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const docSnap = await getDoc(doc(db, 'system_config', 'daraja'));
                if (docSnap.exists()) {
                    setConfig(docSnap.data());
                }
            } catch (e) {
                console.error("Error fetching config:", e);
            }
            setLoading(false);
        };
        fetchConfig();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setStatus('saving');
        try {
            await setDoc(doc(db, 'system_config', 'daraja'), {
                ...config,
                lastUpdated: new Date().toISOString()
            });
            setStatus('success');
            setTimeout(() => setStatus(''), 3000);
        } catch (error) {
            console.error(error);
            setStatus('error');
        }
    };

    if (loading) return <div className="text-zinc-500 font-mono text-xs animate-pulse">Establishing secure tunnel to system_config...</div>;

    return (
        <div className="max-w-4xl space-y-12 pb-20">
            <header>
                <div className="flex items-center gap-3 mb-2">
                    <Shield className="text-brand-primary" size={24} />
                    <h1 className="text-4xl font-black text-white tracking-tighter uppercase italic">Global Ecosystem Config</h1>
                </div>
                <p className="text-zinc-400 font-bold uppercase tracking-widest text-[10px]">Administrative Credentials & Master Logic Toggles</p>
            </header>

            <form onSubmit={handleSave} className="space-y-8">
                {/* DARAJA SECTION */}
                <div className="bg-zinc-900 border border-white/5 rounded-3xl p-10 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                        <Key size={120} />
                    </div>
                    
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-emerald-400 font-black uppercase tracking-widest text-xs mb-1">M-Pesa Daraja Integration</h2>
                            <p className="text-zinc-500 text-[10px] font-bold">LIVE PRODUCTION GATEWAY</p>
                        </div>
                        <button 
                            type="button"
                            onClick={() => setShowKeys(!showKeys)}
                            className="p-2 rounded-xl bg-white/5 text-zinc-400 hover:text-white transition-colors"
                        >
                            {showKeys ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>

                    <div className="space-y-6 relative">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Consumer Key</label>
                                <input 
                                    type={showKeys ? "text" : "password"}
                                    value={config.darajaConsumerKey}
                                    onChange={e => setConfig({ ...config, darajaConsumerKey: e.target.value })}
                                    className="w-full bg-black border border-white/5 rounded-2xl px-6 py-4 text-white font-mono text-sm focus:outline-none focus:border-emerald-500/50 transition-all shadow-inner"
                                    placeholder="Enter Production Consumer Key"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Consumer Secret</label>
                                <input 
                                    type={showKeys ? "text" : "password"}
                                    value={config.darajaConsumerSecret}
                                    onChange={e => setConfig({ ...config, darajaConsumerSecret: e.target.value })}
                                    className="w-full bg-black border border-white/5 rounded-2xl px-6 py-4 text-white font-mono text-sm focus:outline-none focus:border-emerald-500/50 transition-all shadow-inner"
                                    placeholder="Enter Production Consumer Secret"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Online Passkey</label>
                            <input 
                                type={showKeys ? "text" : "password"}
                                value={config.darajaPasskey}
                                onChange={e => setConfig({ ...config, darajaPasskey: e.target.value })}
                                className="w-full bg-black border border-white/5 rounded-2xl px-6 py-4 text-white font-mono text-sm focus:outline-none focus:border-emerald-500/50 transition-all shadow-inner"
                                placeholder="Enter LNM Online Passkey"
                            />
                        </div>

                        <div className="pt-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-6 rounded-full relative transition-colors cursor-pointer ${config.isLive ? 'bg-emerald-500' : 'bg-zinc-700'}`} onClick={() => setConfig({...config, isLive: !config.isLive})}>
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${config.isLive ? 'left-7' : 'left-1'}`} />
                                </div>
                                <div>
                                    <span className="text-xs font-black text-white uppercase tracking-widest">Environment: {config.isLive ? 'PRODUCTION (LIVE)' : 'SANDBOX (TESTING)'}</span>
                                    <p className="text-[9px] text-zinc-500 font-bold">SWITCHING THIS CHANGES THE API TARGET IMMEDIATELY</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* VPRE CONTROL SECTION */}
                <div className="bg-zinc-900 border border-white/5 rounded-3xl p-10 opacity-60 hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-4 mb-6">
                        <Globe size={20} className="text-brand-primary" />
                        <div>
                            <h2 className="text-white font-black uppercase tracking-widest text-xs">VPRE Ledger Integrity</h2>
                            <p className="text-zinc-500 text-[9px] font-bold italic uppercase tracking-widest">Master digital notarization controls</p>
                        </div>
                    </div>
                    
                    <div className="bg-black/40 rounded-2xl p-6 border border-white/5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-zinc-300 font-bold mb-1">Force Synchronous Re-Sync</p>
                                <p className="text-[9px] text-zinc-500">Triggers a manual audit of all VPRE Title Deeds against the Vault.</p>
                            </div>
                            <button type="button" className="p-3 bg-zinc-800 text-zinc-400 rounded-xl hover:text-white hover:bg-zinc-700 transition-all">
                                <RefreshCcw size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between pt-4">
                    <div className="flex items-center gap-2">
                        {status === 'success' && <div className="text-emerald-400 text-xs font-black uppercase tracking-widest flex items-center gap-2 animate-bounce"><CheckCircle size={14}/> Saved Successfully</div>}
                        {status === 'error' && <div className="text-red-400 text-xs font-black uppercase tracking-widest">Save Failed - Insufficient Permissions</div>}
                    </div>
                    
                    <button 
                        type="submit"
                        disabled={status === 'saving'}
                        className="bg-white text-black font-black uppercase tracking-widest text-sm px-10 py-5 rounded-2xl hover:scale-105 active:scale-95 transition-all shadow-xl shadow-white/5 flex items-center gap-3 disabled:opacity-50"
                    >
                        <Save size={18} />
                        {status === 'saving' ? 'Committing...' : 'Commit Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default GlobalConfig;
